import {
  EnumDatiAnagraficiAggiuntiviMetodoLoginUtilizzato,
  EnumProfiloSelezionatoTipo,
} from '@framework/api-client';
import {
  Datatype,
  IFieldDatatypeOption,
  IReference,
  ReferencePathDirection,
  ReferenceType,
} from '@framework/engine';
import { ISessionData } from './ISessionData';

type LegalEntitySessionDataKey =
  | 'selectedProfileLegalEntity'
  | 'spidLegalEntity'
  | 'mergedLegalEntity';

function createSubmitterSessionReference(
  datatype: Datatype.String | Datatype.Number | Datatype.Date,
  code: string,
  label: string,
): IReference {
  return {
    type: ReferenceType.Session,
    id: `richiedente_${code}`,
    code,
    label,
    path: [
      { direction: ReferencePathDirection.Absolute, code: 'session' },
      { direction: ReferencePathDirection.Child, code: 'richiedente' },
    ],
    field: {
      type: datatype,
    },
    group: 'Richiedente',
  };
}

/**
 * @param fieldConfig.options Se specificato, il campo diventa una select
 */
function createSubmitterAdditionalDataReference(
  datatype: Datatype.String | Datatype.Number | Datatype.Date,
  code: string,
  label: string,
  fieldConfig?: {
    options?: IFieldDatatypeOption[];
  },
): IReference {
  return {
    type: ReferenceType.Session,
    id: `additional_data_${code}`,
    code,
    label,
    path: [
      { direction: ReferencePathDirection.Absolute, code: 'session' },
      {
        direction: ReferencePathDirection.Child,
        code: 'datiAnagraficiAggiuntivi' satisfies keyof ISessionData,
      },
    ],
    field: {
      type: datatype,
      ...(fieldConfig?.options
        ? {
            options: fieldConfig.options,
          }
        : {}),
    },
    group: 'Richiedente',
  };
}

function createLegalEntityReference(
  parentCode: LegalEntitySessionDataKey,
  code: string,
  label: string,
  datatype:
    | Datatype.String
    | Datatype.Number
    | Datatype.Boolean = Datatype.String,
): IReference {
  return {
    type: ReferenceType.Session,
    id: `${parentCode}_${code}`,
    code,
    label,
    path: [
      { direction: ReferencePathDirection.Absolute, code: 'session' },
      {
        direction: ReferencePathDirection.Child,
        code: parentCode satisfies keyof ISessionData,
      },
    ],
    field: { type: datatype },
    group: 'Persona Giuridica',
  };
}

function createLegalEntitySetReferences(
  parentCode: LegalEntitySessionDataKey,
  groupPrefix: string,
): IReference[] {
  return [
    createLegalEntityReference(
      parentCode,
      'companyName',
      `${groupPrefix}: Ragione sociale`,
    ),
    createLegalEntityReference(
      parentCode,
      'vatNumber',
      `${groupPrefix}: Partita IVA`,
    ),
    createLegalEntityReference(
      parentCode,
      'address',
      `${groupPrefix}: Indirizzo`,
    ),
    createLegalEntityReference(parentCode, 'postalCode', `${groupPrefix}: CAP`),
    createLegalEntityReference(parentCode, 'country', `${groupPrefix}: Paese`),
    createLegalEntityReference(
      parentCode,
      'province',
      `${groupPrefix}: Provincia`,
    ),
    createLegalEntityReference(
      parentCode,
      'municipality',
      `${groupPrefix}: Comune`,
    ),
    createLegalEntityReference(parentCode, 'peo', `${groupPrefix}: PEO`),
    createLegalEntityReference(parentCode, 'pec', `${groupPrefix}: PEC`),
    createLegalEntityReference(parentCode, 'phone', `${groupPrefix}: Telefono`),
    createLegalEntityReference(
      parentCode,
      'isAccredited',
      `${groupPrefix}: Accreditata`,
      Datatype.Boolean,
    ),
  ];
}

/**
 * Verificare sempre che siano allineate con `ISessionData`.
 * Il prefisso è sempre `session`
 */
export function getSessionDataReferences(): IReference[] {
  return [
    {
      type: ReferenceType.Session,
      id: 'data_compilazione',
      code: 'data_compilazione',
      label: 'Data compilazione',
      path: [{ direction: ReferencePathDirection.Absolute, code: 'session' }],
      field: {
        type: Datatype.Date,
      },
    },
    createSubmitterSessionReference(
      Datatype.String,
      'nome',
      'Richiedente: Nome',
    ),
    createSubmitterSessionReference(
      Datatype.String,
      'cognome',
      'Richiedente: Cognome',
    ),
    createSubmitterSessionReference(
      Datatype.String,
      'email',
      'Richiedente: Email',
    ),
    /** @deprecated */
    // createSubmitterSessionReference(
    //   Datatype.String,
    //   'codiceFiscale',
    //   'Richiedente: Codice fiscale',
    // ),
    createSubmitterSessionReference(
      Datatype.String,
      'codice_fiscale',
      'Richiedente: Codice fiscale',
    ),
    // Dati anagrafici aggiuntivi (SPID)
    createSubmitterAdditionalDataReference(
      Datatype.Date,
      'dataNascita',
      'Richiedente: Data di nascita',
    ),
    createSubmitterAdditionalDataReference(Datatype.String, 'genere', 'Genere'),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'codiceLuogoNascita',
      'Richiedente: Codice luogo di nascita',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'denominazioneLuogoNascita',
      'Richiedente: Denominazione luogo di nascita',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'codiceProvinciaNascita',
      'Richiedente: Codice provincia di nascita',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'denominazioneProvinciaNascita',
      'Richiedente: Denominazione provincia di nascita',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'paeseDiNascita',
      'Richiedente: Paese di nascita',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'indirizzo',
      'Richiedente: Indirizzo',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'indirizzoDigitale',
      'Richiedente: Indirizzo digitale',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'numeroCellulare',
      'Richiedente: Numero di cellulare',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'codiceIva',
      'Richiedente: Partita IVA',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'sedeLegale',
      'Richiedente: Sede legale',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'indirizzoDomicilioFisico',
      'Richiedente: Indirizzo domicilio fisico',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'capDomicilioFisico',
      'Richiedente: CAP domicilio fisico',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'comuneDomicilioFisico',
      'Richiedente: Comune domicilio fisico',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'provinciaDomicilioFisico',
      'Richiedente: Provincia domicilio fisico',
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'nazioneDomicilioFisico',
      'Richiedente: Nazione domicilio fisico',
    ),
    // Carta d'identità
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'tipologiaCi',
      "Richiedente: Tipologia carta d'identità",
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'idCi',
      "Richiedente: ID carta d'identità",
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'comuneRilascioCi',
      "Richiedente: Comune rilascio carta d'identità",
    ),
    createSubmitterAdditionalDataReference(
      Datatype.Date,
      'dataRilascioCi',
      "Richiedente: Data rilascio carta d'identità",
    ),
    createSubmitterAdditionalDataReference(
      Datatype.Date,
      'dataScadenzaCi',
      "Richiedente: Data scadenza carta d'identità",
    ),
    createSubmitterAdditionalDataReference(
      Datatype.String,
      'metodoLoginUtilizzato',
      'Richiedente: Metodo di login utilizzato',
      {
        options: [
          {
            label: 'SPID',
            value: EnumDatiAnagraficiAggiuntiviMetodoLoginUtilizzato.SPID,
          },
          {
            label: 'CIE',
            value: EnumDatiAnagraficiAggiuntiviMetodoLoginUtilizzato.CIE,
          },
          {
            label: 'CNS',
            value: EnumDatiAnagraficiAggiuntiviMetodoLoginUtilizzato.CNS,
          },
          {
            label: 'eIDAS',
            value: EnumDatiAnagraficiAggiuntiviMetodoLoginUtilizzato.EIDAS,
          },
          {
            label: 'LDAP (Dipendente)',
            value: EnumDatiAnagraficiAggiuntiviMetodoLoginUtilizzato.LDAP,
          },
          {
            label: 'Extra UE',
            value: EnumDatiAnagraficiAggiuntiviMetodoLoginUtilizzato.EXTRA_UE,
          },
        ],
      },
    ),
    // Profilo selezionato (PF/PG)
    {
      type: ReferenceType.Session,
      id: 'selectedProfileType',
      code: 'selectedProfileType',
      label: 'Profilo selezionato (PF/PG)',
      path: [{ direction: ReferencePathDirection.Absolute, code: 'session' }],
      field: {
        type: Datatype.String,
        options: [
          { label: 'Persona Fisica', value: EnumProfiloSelezionatoTipo.PF },
          { label: 'Persona Giuridica', value: EnumProfiloSelezionatoTipo.PG },
        ],
      },
    },
    // Set A: PG dal profilo selezionato (DB)
    ...createLegalEntitySetReferences(
      'selectedProfileLegalEntity',
      'PG Profilo',
    ),
    // Set B: PG dall'asserzione SPID
    ...createLegalEntitySetReferences('spidLegalEntity', 'PG SPID'),
    // Set C: PG merge SPID/DB, da usare per interfaccia
    ...createLegalEntitySetReferences('mergedLegalEntity', 'PG'),
  ];
}
